import { marked } from "marked";
import { processAnswer } from "../../../utilities/apis/chat";
import { MOCK_ANSWER } from "./chatMockData";
// Hook to get chat service instance
import { useChat } from "../contexts/ChatContext";
import { useRef } from "react";

// Shared chat logic that both sidebar and popup can use
export class ChatService {
  constructor(chatContext) {
    this.chat = chatContext;
    this.fileIds = null; // Store agreement ids for multi-doc chat
  }

  // Initialize chat with conversation ID and suggestions
  async initializeChat(
    conversationID,
    initialSuggestions = [],
    fileIds = null,
  ) {
    this.chat.setConversationID(conversationID);
    this.chat.setInitialSuggestions(initialSuggestions);
    this.fileIds = fileIds;

    // Only reset chat if there are no existing messages or if conversationID changed
    const shouldReset =
      this.chat.messages.length === 0 ||
      this.chat.conversationID !== conversationID;

    if (shouldReset) {
      this.chat.resetChat({ conversationID, initialSuggestions });
    }

    // Load any saved input draft
    const draft = this.chat.loadInputDraft(conversationID);
    if (draft) {
      this.chat.setInputDraft(draft);
    }
  }

  // Send a message
  async sendMessage(questionText, conversationID = null) {
    const convID = conversationID || this.chat.conversationID;

    if (!questionText.trim()) return;

    // Check if chat is locked to prevent duplicate requests
    if (this.chat.isChatLocked()) {
      console.warn(
        "Chat is locked, cannot send message while another request is in progress",
      );
      // Optionally show user feedback
      this.chat.setError("Please wait for the current request to complete");
      setTimeout(() => this.chat.setError(null), 3000);
      return;
    }

    const userMessageId = Date.now();
    const loadingMessageId = userMessageId + 1;

    // Add user message and loading indicator
    this.chat.addMessage({
      id: userMessageId,
      sender: "user",
      text: questionText,
    });

    this.chat.addMessage({
      id: loadingMessageId,
      sender: "ai",
      text: "",
      isLoading: true,
    });

    this.chat.setError(null);
    this.chat.setLoading(true);

    // Clear input draft after sending
    this.chat.clearInputDraft(convID);

    try {
      let response = MOCK_ANSWER;

      if (questionText !== "test") {
        response = await processAnswer({
          question: questionText,
          conversationID: convID,
          fileIds: this.fileIds,
        });
      }

      const answerText = response.data.answer || "No response.";
      const suggestions = response.data.suggestions || [];
      const newConversationID = response.data.conversation_id || convID;
      const documentReferences = response.data.document_references || []; // Document references for the answers

      // Update conversation ID if it changed
      if (newConversationID !== convID) {
        this.chat.setConversationID(newConversationID);
      }

      // Parse markdown to HTML
      const parsedAnswer = marked(answerText);

      // Remove loading message and add actual response
      this.chat.removeMessage(loadingMessageId);
      this.chat.addMessage({
        id: Date.now(),
        sender: "ai",
        text: parsedAnswer,
        suggestions: suggestions,
        conversationID: newConversationID,
        documentReferences: documentReferences,
      });
    } catch (err) {
      console.error("Chat error:", err.message);

      // Remove loading message and add error
      this.chat.removeMessage(loadingMessageId);
      this.chat.addMessage({
        id: Date.now(),
        sender: "ai",
        text: `Error: ${err.message}`,
      });

      this.chat.setError("Failed to get response.");
    } finally {
      this.chat.setLoading(false);
    }
  }

  // Handle suggestion click
  async handleSuggestionClick(suggestion, conversationID = null) {
    await this.sendMessage(suggestion, conversationID);
  }

  // Copy message to clipboard
  copyMessage(text) {
    // Convert HTML to plain text
    const normalized = text
      .replace(/<br\s*\/?\s*>/gi, "\n")
      .replace(/<\/p>\s*<p>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\r\n/g, "\n")
      .replace(/\n{2,}/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .trim();

    try {
      navigator.clipboard.writeText(normalized);

      return true;
    } catch (e) {
      // Fallback: prompt user with text to copy
      window.prompt("Copy the message below:", normalized);
      return false;
    }
  }

  // Rerun a previous message
  async rerunMessage(previousQuestion, conversationID = null) {
    await this.sendMessage(previousQuestion, conversationID);
  }

  // Get the latest AI message ID for UI state management
  getLatestAIMessageId() {
    return this.chat.messages
      .slice()
      .reverse()
      .find((m) => m.sender === "ai")?.id;
  }

  // Update input draft
  updateInputDraft(draft) {
    this.chat.setInputDraft(draft);
  }

  // Get current input draft
  getInputDraft() {
    return this.chat.inputDraft;
  }

  // Check if sending is disabled
  isSendDisabled() {
    return this.chat.loading || !this.chat.inputDraft.trim();
  }
}

export function useChatService() {
  const chat = useChat();
  const serviceRef = useRef(null);

  // Create service instance only once and update its chat reference
  if (!serviceRef.current) {
    serviceRef.current = new ChatService(chat);
  } else {
    // Update the chat reference in existing service
    serviceRef.current.chat = chat;
  }

  return serviceRef.current;
}
